import { empty, isset, strval, in_array, intval, is_numeric, phpCopy } from '../../php-compat';
import type { PortEnv, PhpRef } from '../../port-contract';
import { slot, isArray, array, values, type Arr } from './access';
import { catalogVariableName, catalogRows } from './catalog';
import { casterCanReadWrittenLanguage, type LuckSkillDeps } from './luck_and_skill_roll_helpers';
export interface BookGateDeps extends LuckSkillDeps {
    inventory_field(row: Arr, field: string): unknown;
    possessor_type_of(row: Arr): unknown;
    query_d_item_arr(id: string): Arr;
    get_inventory_arr(id: string): Arr;
    fetch_rolls(filters: Arr): Arr;
    caster_skill_entry(caster: Arr, skill: string): Arr | null;
    get_item_id_name(id: string): unknown;
}
/** PHP: {closure@59:17} (includes/functions/janus/rolls/book_reading_gate_helpers.php:59) */
export function closure59BookOwnerWalk(inventory: Arr, visited: PhpRef<Arr>, env: PortEnv<BookGateDeps>): string {
    const id = env.deps.inventory_field(inventory, 'inventory_id') ?? '';
    if (id === '' || isset(visited.value, strval(id)))
        return '';
    const seen = array(visited.value);
    seen.set(strval(id), true);
    visited.value = seen;
    const type = env.deps.possessor_type_of(inventory) ?? '', possessor = env.deps.inventory_field(inventory, 'possess_id') ?? '';
    if (!env.deps.check_uuid(possessor))
        return '';
    if (in_array(type, ['encounter_creature', 'creature'], true))
        return possessor as string;
    if (type === 'player_character') {
        const pc = env.deps.query_d_item_arr(possessor as string);
        if (empty(pc))
            return '';
        const ec = slot(pc, 'encounter_creatures', 'encounter_creature_id') ?? slot(pc, 'player_characters', 'encounter_creature_id') ?? '';
        return env.deps.check_uuid(strval(ec)) ? strval(ec) : '';
    }
    if (type === 'inventory') {
        const parent = env.deps.get_inventory_arr(possessor as string);
        return empty(parent) ? '' : closure59BookOwnerWalk(parent, visited, env);
    }
    return '';
}
/** PHP: resolve_encounter_creature_id_for_inventory_book_context (includes/functions/janus/rolls/book_reading_gate_helpers.php:56) */
export function resolveEncounterCreatureIdForInventoryBookContext(inventory: Arr, env: PortEnv<BookGateDeps>): string { return closure59BookOwnerWalk(inventory, { value: [] }, env); }
/** PHP: book_inventory_finish_reading_tracking_roll_exists (includes/functions/janus/rolls/book_reading_gate_helpers.php:105) */
export function bookInventoryFinishReadingTrackingRollExists(roller: string, inventory: string, campaign: string | null = null, env: PortEnv<BookGateDeps>): boolean {
    if (!env.deps.check_uuid(roller) || !env.deps.check_uuid(inventory))
        return false;
    const filters = array({ roll_by: roller, rolls_full: 'ft_finish_read_inv:' + inventory, roll_deleted: 0, limit: 1 });
    if (env.deps.check_uuid(strval(campaign)))
        filters.set('campaign_id', campaign);
    filters.set('roll_type_1', 'finish_reading');
    return !empty(env.deps.fetch_rolls(filters));
}
/** PHP: book_training_book_tier_allowed_skill_tier_band (includes/functions/janus/rolls/book_reading_gate_helpers.php:159) */
export function bookTrainingBookTierAllowedSkillTierBand(tier: number): {low: number; high: number} | null { return tier < 1 ? null : tier === 1 ? { low: 0, high: 2 } : { low: 2 * tier - 1, high: 2 * tier }; }
/** PHP: book_default_written_in_language_item_id (includes/functions/janus/rolls/book_reading_gate_helpers.php:209) */
export function bookDefaultWrittenInLanguageItemId(env: PortEnv<BookGateDeps>): string {
    const key = 'book_default_written_in_language_item_id::$cached';
    if (!(key in env.functionStatics)) env.functionStatics[key] = null;
    if (env.functionStatics[key] !== null) return strval(env.functionStatics[key]);
    for (const row of catalogRows('Language').values())
        if ((slot(row, 'variable_name') ?? '') === 'common') {
            const id = strval(slot(row, 'item_id') ?? '');
            if (id !== '' && env.deps.check_uuid(id))
                return env.functionStatics[key] = id;
            break;
        }
    return env.functionStatics[key] = '9CE184D4-669F-C2FC-1FA3-F6D3E55561A0';
}
/** PHP: book_effective_written_language_item_id (includes/functions/janus/rolls/book_reading_gate_helpers.php:231) */
export function bookEffectiveWrittenLanguageItemId(book: Arr, env: PortEnv<BookGateDeps>): string {
    const language = strval(env.deps.layer_value(book, 'inventory_written_in_language') ?? env.deps.layer_value(book, 'written_in_language') ?? '');
    return language !== '' && env.deps.check_uuid(language) ? language : bookDefaultWrittenInLanguageItemId(env);
}
/** PHP: book_written_language_gate_message (includes/functions/janus/rolls/book_reading_gate_helpers.php:248) */
export function bookWrittenLanguageGateMessage(book: Arr, caster: Arr, env: PortEnv<BookGateDeps>): string | null {
    const language = bookEffectiveWrittenLanguageItemId(book, env);
    if (!env.deps.check_uuid(language))
        return 'You need at least skill tier 2 in the book’s written language to read it.';
    return casterCanReadWrittenLanguage(caster, language, 2, env) ? null : 'You need at least skill tier 2 in ' + strval(env.deps.get_item_id_name(language)) + ' to read this book.';
}
import { catalogVariable as b3catalogVariable, type Row as b3Row, type PortEnv as b3PortEnv, type PhpRef as b3PhpRef, get as b3get, isArray as b3isArray, empty as b3empty, intval as b3intval, strval as b3strval, is_numeric as b3is_numeric, LANGUAGES as b3LANGUAGES, values as b3values } from './support-b3';
interface B3Deps {
    check_uuid(id: string): boolean;
    fetch_rolls(filter: b3Row): unknown;
    layer_value(row: unknown, key: string): unknown;
    'Skill::getVariableName'(id: unknown): unknown;
    caster_skill_entry(row: b3Row, skill: unknown): unknown;
    inventory_field(row: b3Row, key: string): unknown;
    possessor_type_of(row: b3Row): unknown;
    query_d_item_arr(id: unknown): unknown;
    get_inventory_arr(id: unknown): unknown;
    caster_can_read_written_language(caster: b3Row, id: string, tier: number): boolean;
    get_item_id_name(id: string): string;
}
/** PHP: book_training_book_tier_gate_message (includes/functions/janus/rolls/book_reading_gate_helpers.php:178) */
export function bookTrainingBookTierGateMessage(book: b3Row, caster: b3Row, env: b3PortEnv<B3Deps>): string | null {
    const raw = b3strval(env.deps.layer_value(book, 'training_book_tier') ?? '');
    if (raw === '' || !b3is_numeric(raw))
        return null;
    const band = bookTrainingBookTierAllowedSkillTierBand(Number(b3intval(raw)));
    if (band === null)
        return null;
    const skill = env.deps.layer_value(book, 'skill_trained') ?? '';
    if (!env.deps.check_uuid(b3strval(skill)))
        return null;
    const vn = b3catalogVariable('Skill', skill as string, env);
    if (vn === null || vn === '')
        return 'This book’s skill cannot be checked against training book tier.';
    const current = Number(b3intval(b3get(env.deps.caster_skill_entry(caster, vn), 'tier_level') ?? 0));
    return current >= band.low && current <= band.high ? null : `This book only trains skill tiers ${band.low}–${band.high}. Your current tier for this skill does not qualify.`;
}
